import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, UserPlus, FileText, Bell, Users, FormInput, MessageSquareReply, ShieldAlert } from "lucide-react";

export default function AdminActivity() {
  const [data, setData] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [act, eng] = await Promise.all([
        api.get("/admin/activity", { params: { days } }),
        api.get("/admin/buyer-engagement", { params: { days } }),
      ]);
      setData(act.data);
      setEngagement(eng.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading || !data) {
    return (
      <div className="card p-8 text-center text-[var(--ink-2)]">
        <Loader2 size={20} className="animate-spin inline" /> Loading activity…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="admin-activity">
      <div className="flex gap-2 justify-end">
        {[1, 7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            className={"btn " + (days === d ? "btn-primary" : "btn-ghost")}
            onClick={() => setDays(d)}
            data-testid={`admin-activity-range-${d}`}
          >
            {d}d
          </button>
        ))}
        <button type="button" className="btn btn-ghost" onClick={refresh}>
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Tile icon={UserPlus} label="New signups" value={data.new_users} testid="activity-signups" />
        <Tile icon={Users} label="New leads" value={data.new_leads} testid="activity-leads" />
        <Tile icon={Bell} label="Deal alerts" value={data.new_alerts} testid="activity-alerts" />
        <Tile icon={FileText} label="Reports / snapshots" value={data.new_reports} testid="activity-reports" />
        <Tile icon={FormInput} label="Captures" value={data.new_captures} testid="activity-captures" />
      </div>

      {/* Buyer engagement scorecard — platform-wide nudge → reply rate */}
      {engagement && (
        <div className="card p-4" data-testid="admin-buyer-engagement">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide flex items-center gap-1.5">
              <MessageSquareReply size={12} /> Buyer engagement · last {engagement.window_days}d
            </div>
            <div className="text-[10px] text-[var(--ink-3)]">Min 3 nudges to qualify for leaderboard</div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            <MiniStat label="Nudges sent" value={engagement.sent} testid="bengage-sent" />
            <MiniStat label="Replied" value={engagement.replied} testid="bengage-replied" />
            <MiniStat
              label="Reply rate"
              value={`${engagement.reply_rate}%`}
              accent
              testid="bengage-reply-rate"
            />
            <MiniStat label="YES" value={engagement.yes} testid="bengage-yes" />
            <MiniStat
              label="YES of replies"
              value={`${engagement.yes_rate}%`}
              testid="bengage-yes-rate"
            />
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-2">Top users by reply rate</div>
          {(engagement.top_users || []).length === 0 ? (
            <div className="text-sm text-[var(--ink-3)] py-4 text-center">No accounts hit the 3-nudge threshold yet.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {engagement.top_users.map((u, i) => (
                <div key={u.user_id} className="flex items-center justify-between text-sm border-b border-[var(--line)] pb-2 last:border-b-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold">{i + 1}</div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate max-w-[220px]">{u.name || u.email}</div>
                      <div className="text-xs text-[var(--ink-2)] truncate max-w-[220px]">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-xs text-[var(--ink-3)]">{u.replied}/{u.sent}</div>
                    <div className="font-display font-extrabold text-lg tabular-nums text-[var(--primary)]">{u.reply_rate}%</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(engagement.paused_users || []).length > 0 && (
            <div className="mt-4 pt-3 border-t border-[var(--line)]" data-testid="admin-paused-users">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-700 mb-2">
                <ShieldAlert size={11} /> Reputation-paused accounts ({engagement.paused_users.length})
              </div>
              <div className="flex flex-col gap-1.5">
                {engagement.paused_users.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between text-xs">
                    <div className="min-w-0">
                      <div className="font-semibold truncate max-w-[220px]">{u.name || u.email}</div>
                      <div className="text-[var(--ink-3)] truncate max-w-[220px]">{u.email}</div>
                    </div>
                    <div className="text-amber-700 font-semibold shrink-0">
                      {u.reply_rate_at_pause}% on {u.sent_at_pause} sent
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="card p-4" data-testid="admin-top-users">
          <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide mb-3">Top users by leads</div>
          {data.top_users_by_leads.length === 0 ? (
            <div className="text-sm text-[var(--ink-3)] py-6 text-center">No activity in this window.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {data.top_users_by_leads.map((u, i) => (
                <div key={u.user_id} className="flex items-center justify-between text-sm border-b pb-2 last:border-b-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate max-w-[200px]">{u.name || u.email}</div>
                      <div className="text-xs text-[var(--ink-2)] truncate max-w-[200px]">{u.email}</div>
                    </div>
                  </div>
                  <div className="font-display font-extrabold text-lg tabular-nums">{u.leads}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card p-4" data-testid="admin-recent-signups">
          <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide mb-3">Recent signups</div>
          {data.recent_signups.length === 0 ? (
            <div className="text-sm text-[var(--ink-3)] py-6 text-center">No new signups in this window.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {data.recent_signups.map((u) => (
                <div key={u.id} className="flex items-center justify-between text-sm border-b pb-2 last:border-b-0">
                  <div className="min-w-0">
                    <div className="font-semibold truncate max-w-[220px]">{u.name || u.email}</div>
                    <div className="text-xs text-[var(--ink-2)] truncate max-w-[220px]">{u.email}</div>
                  </div>
                  <div className="text-xs text-[var(--ink-3)]">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <Icon size={16} className="text-[var(--ink-2)] mb-2" />
      <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide">{label}</div>
      <div className="font-display font-black text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, accent, testid }) {
  return (
    <div
      className={"rounded-lg border px-3 py-2.5 " + (accent ? "bg-[var(--primary)] border-transparent text-white" : "bg-[var(--bg-2)] border-[var(--line)]")}
      data-testid={testid}
    >
      <div className={"text-[10px] uppercase tracking-wider " + (accent ? "text-white/75" : "text-[var(--ink-3)]")}>{label}</div>
      <div className={"font-display font-extrabold text-lg tabular-nums mt-0.5 " + (accent ? "text-white" : "text-[var(--ink)]")}>{value}</div>
    </div>
  );
}
